import { type ReactNode, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ItemTooltip } from '../routes/app/content/-editor/item-tooltip';
import { type DropdownItemHoverVariant, DropdownMenuItem } from './dropdown-menu';

export type MenuOptionProps = {
  className?: string;
  textClassName?: string;
  mainText: string;
  secondaryText?: string;
  leftElement?: ReactNode;
  rightElement?: ReactNode;
  variant?: 'red' | 'gray' | 'accent' | 'light-gray';
  disabled?: boolean;
  isActive?: boolean;
};

function getVariantClassNames(variant: MenuOptionProps['variant']) {
  return {
    hover:
      variant === 'gray' || variant === 'light-gray'
        ? 'group-hover:text-branding-accent-700'
        : 'group-hover:text-red-700',
    focus:
      variant === 'gray' || variant === 'light-gray'
        ? 'group-focus:text-branding-accent-700'
        : 'group-focus:text-red-700',
    text:
      variant === 'gray' ? 'text-black' : variant === 'light-gray' ? 'text-gray' : 'text-red-700',
    active:
      variant === 'gray' || variant === 'light-gray' ? 'text-branding-accent-700' : 'text-red-700',
  };
}

export function MenuOption(props: MenuOptionProps) {
  const { variant = 'gray', isActive = false } = props;
  const [isRenderMainTextAsTooltip, setIsRenderMainTextAsTooltip] = useState(false);
  const [isRenderSecondaryTextAsTooltip, setIsRenderSecondaryTextAsTooltip] = useState(false);
  const optionClassNames = twMerge([
    'px-3 py-2 flex justify-between items-center rounded-md bg-transparent gap-2 w-60 group outline-none overflow-hidden focus:outline-none',
    ...(props.disabled
      ? ['bg-transparent cursor-default text-gray']
      : [
          getVariantClassNames(variant).hover,
          getVariantClassNames(variant).focus,
          'cursor-pointer',
        ]),
    ...(isActive && !props.disabled
      ? [getVariantClassNames(variant).active, 'cursor-default']
      : ['']),
    props.className ?? '',
  ]);
  const mainTextClassNames = twMerge(
    'font-body-2-m',
    ...(props.disabled
      ? ['text-gray']
      : [
          getVariantClassNames(variant).hover,
          getVariantClassNames(variant).focus,
          getVariantClassNames(variant).text,
        ]),
    ...(isActive && !props.disabled ? [getVariantClassNames(variant).active] : ['']),
    'max-w-[90%] truncate',
    props.textClassName ?? ''
  );
  const secondaryTextClassNames = twMerge(
    'font-body-3-r',
    ...(props.disabled
      ? ['text-gray']
      : [
          getVariantClassNames(variant).hover,
          getVariantClassNames(variant).focus,
          getVariantClassNames(variant).text,
        ]),
    ...(isActive && !props.disabled ? [getVariantClassNames(variant).active] : ['']),
    'max-w-[90%] truncate'
  );
  return (
    <div className={optionClassNames} tabIndex={0}>
      {props.leftElement != null ? props.leftElement : null}
      <div className="flex-1 overflow-hidden text-left">
        <ItemTooltip text={props.mainText} renderAsTooltip={isRenderMainTextAsTooltip}>
          <p
            className={mainTextClassNames}
            onMouseEnter={function () {
              setIsRenderMainTextAsTooltip(props.mainText.length > 21);
            }}
            onMouseLeave={function () {
              setIsRenderMainTextAsTooltip(false);
            }}
          >
            {props.mainText}
          </p>
        </ItemTooltip>
        {props.secondaryText != null ? (
          <ItemTooltip text={props.secondaryText} renderAsTooltip={isRenderSecondaryTextAsTooltip}>
            <p
              className={secondaryTextClassNames}
              onMouseEnter={function () {
                if (props.secondaryText == null) {
                  setIsRenderMainTextAsTooltip(false);
                  return;
                }
                setIsRenderSecondaryTextAsTooltip(props.secondaryText.length > 34);
              }}
              onMouseLeave={function () {
                setIsRenderMainTextAsTooltip(false);
              }}
            >
              {props.secondaryText}
            </p>
          </ItemTooltip>
        ) : null}
      </div>
      {props.rightElement != null ? props.rightElement : null}
    </div>
  );
}

export function MenuOptionIcon(props: {
  children: ReactNode;
  disabled?: boolean;
  isActive?: boolean;
  variant?: MenuOptionProps['variant'];
}) {
  const colorClassNames = twMerge([
    getVariantClassNames(props.variant).hover,
    getVariantClassNames(props.variant).focus,
    getVariantClassNames(props.variant).text,
    props.isActive ? getVariantClassNames(props.variant).active : 'text-black',
  ]);
  return (
    <div className={twMerge(props.disabled ? 'text-gray' : colorClassNames)}>{props.children}</div>
  );
}

export type DropdownMenuOptionProps = MenuOptionProps & {
  onClick?: () => unknown;
  hoverVariant?: DropdownItemHoverVariant;
};

export function DropdownMenuOption(props: DropdownMenuOptionProps) {
  const { onClick, hoverVariant, ...menuOptionProps } = props;
  return (
    <DropdownMenuItem
      className={twMerge('w-full min-w-[132px] p-0', props.className ?? '')}
      onClick={onClick}
      hoverVariant={hoverVariant}
      label={<MenuOption {...menuOptionProps} />}
    />
  );
}
